import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  FAVICON_PATH,
  OG_IMAGE_PATH,
  HREFLANGS,
  absoluteAssetUrl,
  canonicalUrl,
  descriptionKeyForPath,
  publicAsset,
  titleKeyForPath,
} from "@/lib/siteMeta";

const FAVICON_RELS = ["icon", "shortcut icon", "apple-touch-icon"];

function upsertMeta(attr, key, content) {
  if (!content) return;
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  if (el.getAttribute("content") !== content) {
    el.setAttribute("content", content);
  }
}

function upsertLink(rel, href, extra = {}) {
  if (!href) return;
  const selector = rel === "canonical"
    ? 'link[rel="canonical"]'
    : `link[rel="${rel}"][hreflang="${extra.hreflang || ""}"]`;
  let el = document.querySelector(selector);
  if (!el) {
    el = document.createElement("link");
    el.rel = rel;
    if (extra.hreflang) el.hreflang = extra.hreflang;
    document.head.appendChild(el);
  }
  if (el.getAttribute("href") !== href) {
    el.setAttribute("href", href);
  }
}

function upsertJsonLd(id, data) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.id = id;
    el.type = "application/ld+json";
    document.head.appendChild(el);
  }
  const next = JSON.stringify(data);
  if (el.textContent !== next) {
    el.textContent = next;
  }
}

function ensureFavicon(href) {
  for (const rel of FAVICON_RELS) {
    let link = document.querySelector(`link[rel="${rel}"]`);
    if (!link) {
      link = document.createElement("link");
      link.rel = rel;
      document.head.appendChild(link);
    }
    if (link.getAttribute("href") !== href) {
      link.setAttribute("href", href);
    }
    link.type = "image/png";
  }
}

function syncHreflang(pathname) {
  document.querySelectorAll('link[rel="alternate"][data-iyaf-hreflang]').forEach((n) => n.remove());
  const url = canonicalUrl(pathname);
  for (const lang of HREFLANGS) {
    const link = document.createElement("link");
    link.rel = "alternate";
    link.hreflang = lang;
    link.href = url;
    link.setAttribute("data-iyaf-hreflang", "1");
    document.head.appendChild(link);
  }
  const xDefault = document.createElement("link");
  xDefault.rel = "alternate";
  xDefault.hreflang = "x-default";
  xDefault.href = url;
  xDefault.setAttribute("data-iyaf-hreflang", "1");
  document.head.appendChild(xDefault);
}

export function useSiteMeta() {
  const { pathname } = useLocation();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const title = t(titleKeyForPath(pathname));
    const description = t(descriptionKeyForPath(pathname));
    const url = canonicalUrl(pathname);
    const image = absoluteAssetUrl(OG_IMAGE_PATH);

    document.title = title;
    document.documentElement.lang = i18n.language || "en";

    ensureFavicon(publicAsset(FAVICON_PATH));
    upsertMeta("name", "description", description);
    upsertLink("canonical", url);
    syncHreflang(pathname);

    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:site_name", "IYAF");
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:image", image);
    upsertMeta("property", "og:locale", i18n.language === "ar" ? "ar_JO" : `${i18n.language || "en"}_US`);

    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", image);

    upsertJsonLd("iyaf-org-jsonld", {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "International Youth Ambassadors Foundation",
      alternateName: "IYAF",
      url: "https://iyafglobal.com/",
      logo: absoluteAssetUrl(OG_IMAGE_PATH),
      email: "info@iyafglobal.com",
      address: {
        "@type": "PostalAddress",
        streetAddress: "44 Queen Noor Street",
        addressLocality: "Amman",
        postalCode: "11190",
        addressCountry: "JO",
      },
      sameAs: [
        "https://www.linkedin.com/company/iyaf",
        "https://www.instagram.com/iyaf.global/",
      ],
    });
  }, [pathname, t, i18n.language]);
}
